import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Spinner from '../Utilities/components/Spinner';
import { fetchUserData, fetchAllServices, fetchFinancialSummary } from '../Utilities/api';
import OnboardingProfilePrompt from './AddProfileModal/OnboardingProfilePrompt';
import AddPetPrompt from './AddProfileModal/AddPetPrompt';

export const StateContext = React.createContext();

const isLocal =
  (window.location.hostname === 'localhost') ||
  (window.location.hostname === '127.0.0.1') ||
  (/^192\.168\./.test(window.location.hostname));

export const API_BASE_URL = isLocal
  ? 'http://localhost:6060'
  : 'https://www.pawsible.app';

function convertToUserTimeZone(utcDate) {
  var localDate = moment.utc(utcDate).tz('America/Los_Angeles');
  return localDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
}

// ---- serviceDetails caching + retry (no chaining) ----
function loadServicesFromCache() {
  try {
    var raw = localStorage.getItem('serviceDetails_v1');
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed) return null;
    if (!parsed.__ts) return null;
    var freshMs = 60 * 60 * 1000; // 1h
    if ((Date.now() - parsed.__ts) > freshMs) return null;
    return parsed.data;
  } catch (e) {
    return null;
  }
}

function saveServicesToCache(data) {
  try {
    localStorage.setItem('serviceDetails_v1', JSON.stringify({
      __ts: Date.now(),
      data: data
    }));
  } catch (e) {}
}

async function fetchAllServicesWithRetry(maxAttempts) {
  var attempt = 0;
  var delay = 500; // ms
  while (attempt < maxAttempts) {
    try {
      var details = await fetchAllServices();
      if (details && (typeof details === 'object')) {
        return details;
      }
      throw new Error('Empty service details');
    } catch (err) {
      attempt = attempt + 1;
      if (attempt >= maxAttempts) {
        throw err;
      }
      await new Promise(function (res) { setTimeout(res, delay); });
      delay = delay * 2;
      if (delay > 4000) delay = 4000;
    }
  }
  throw new Error('Unreachable');
}

const StateProvider = ({ children, loggedIn, setLoggedIn, role, setRole }) => {
  const navigate = useNavigate();

  // Core state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cart , setCart] = useState([]);
  const [file, setFile] = useState(null);
  const [serviceDetails, setServiceDetails] = useState({}); // always an object
  const [financialSummary, setFinancialSummary] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Cart totals
  const [cartTotal, setCartTotal] = useState(0);
  const [cartOriginalTotal, setCartOrigialTotal] = useState(0);
  const [cartTotalDiscount, setCartTotalDiscount] = useState(0);

  // Checkout balance
  const [useBalance, setUseBalance] = useState(false);
  const [remainingBalance, setRemainingBalance] = useState(0);

  // Onboarding prompts
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showAddPetsModal, setShowAddPetsModal] = useState(false);

  // Booking Data
  const [bookingData, setBookingData] = useState({
    bookingType: localStorage.getItem('bookingType') || '',
  });

  useEffect(() => {
    const auth = getAuth();
    var isMounted = true;

    async function getFinancialSummary() {
      try {
        const details = await fetchFinancialSummary();
        if (isMounted) {
          setFinancialSummary(details);
        }
      } catch (error) {
        console.error('Error fetching financial summary:', error);
      }
    }

    async function robustGetServiceDetails() {
      // 1) Fast hydrate from cache if available
      var cached = loadServicesFromCache();
      if (cached && isMounted) {
        setServiceDetails(cached);
      }

      // 2) Fresh pull with retry; if it fails and no cache, set {}
      try {
        const fresh = await fetchAllServicesWithRetry(3);
        if (isMounted) {
          setServiceDetails(fresh || {}); // guarantee object
          saveServicesToCache(fresh || {});
        }
      } catch (err) {
        console.error('Error fetching service details:', err);
        if (!cached && isMounted) {
          setServiceDetails({});
        }
      }
    }

    async function syncUserData(userData) {
      try {
        await axios.post(API_BASE_URL + '/api/users/sync', userData);
        return true;
      } catch (error) {
        console.error('Failed to sync user data:', error);
        return false;
      }
    }

    async function fetchData(uid) {
      setLoading(true);
      try {
        const userData = await fetchUserData(uid);

        const userAsFamilyMember = {
          name: userData.username ? userData.username.split(' ')[0] : 'Name',
          type: 'Human',
          image: userData.image || 'https://pawsible-s3.s3.us-west-1.amazonaws.com/user_files/defaultPerson.png'
        };

        var filteredMembers = [];
        var i;
        for (i = 0; i < userData.FamilyMembers.length; i++) {
          var m = userData.FamilyMembers[i];
          if (m && m.deletedAt === null) {
            filteredMembers.push(m);
          }
        }
        // sort without chaining
        filteredMembers.sort(function(a, b) {
          if (a.type === 'Human' && b.type !== 'Human') return -1;
          if (a.type !== 'Human' && b.type === 'Human') return 1;
          return String(a.name).localeCompare(String(b.name));
        });

        const sortedFamilyMembers = [userAsFamilyMember].concat(filteredMembers);

        // Convert bookings to local time
        var localBookings = [];
        var j;
        for (j = 0; j < userData.Bookings.length; j++) {
          var bk = userData.Bookings[j];
          localBookings.push({
            id: bk.id,
            // copy fields explicitly to avoid surprises
            ...bk,
            startTime: convertToUserTimeZone(bk.startTime),
            endTime: convertToUserTimeZone(bk.endTime)
          });
        }

        // Active payment methods
        var activePaymentMethods = [];
        var k;
        for (k = 0; k < userData.PaymentMethods.length; k++) {
          var pm = userData.PaymentMethods[k];
          if (pm && pm.deletedAt === null) {
            activePaymentMethods.push(pm);
          }
        }

        const mappedUserData = {
          id: userData.id,
          uid: userData.uid,
          username: userData.username,
          email: userData.email,
          image: userData.image || '',
          photoURL: userData.image || '',
          phone: userData.phone,
          password: userData.password,
          createdAt: userData.createdat,
          updatedAt: userData.updatedat,
          wallet: userData.Wallet,
          daycareBundle: userData.DaycareBundles,
          clubMembership: userData.ClubMembership,
          userFriends: userData.UserFriends,
          cartItems: userData.CartItems,
          bookings: localBookings,
          familyMembers: sortedFamilyMembers,
          paymentMethods: activePaymentMethods,
          stripeCustomerId: userData.stripeCustomerId,
          salesmateId: userData.salesmateId,
          defaultPaymentMethodId: userData.defaultPaymentMethodId,
          referralCode: userData.referralCode,
          referredBy: userData.referredBy,
          referralsCount: userData.referralsCount,
          storeLocation: userData.storeLocation,
          profileComplete: userData.profilecomplete,
          info: {
            email: userData.email || '',
            phone: userData.phone || '',
            addressOne: userData.addressOne || '',
            addressTwo: userData.addressTwo || '',
            city: userData.city || '',
            state: userData.state || '',
            zipCode: userData.zipCode || '',
            job: userData.job || '',
            role: userData.role || ''
          }
        };

        if (isMounted) {
          setUser(function(prevData) {
            var merged = {};
            var key;
            for (key in prevData) { merged[key] = prevData[key]; }
            for (key in mappedUserData) { merged[key] = mappedUserData[key]; }
            return merged;
          });
          setRole(userData.role);
          setCart(userData.CartItems);
          setPaymentMethods(activePaymentMethods);
          setRemainingBalance(userData.Wallet ? userData.Wallet.balance : 0);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error.response || error);
        if (isMounted) setLoading(false);
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async function(current) {
      if (current) {
        const uid = current.uid;
        const email = current.email;
        const displayName = current.displayName || '';
        const authPhotoURL = current.photoURL || '';

        try {
          const userData = await fetchUserData(uid);

          // Prefer server image if it exists, otherwise Firebase Google photo
          var resolvedPhoto = '';
          if (userData && userData.image) {
            resolvedPhoto = userData.image;
          } else if (authPhotoURL) {
            resolvedPhoto = authPhotoURL;
          } else {
            resolvedPhoto = '';
          }

          if (isMounted) {
            setUser({
              uid: uid,
              email: email,
              displayName: displayName,
              photoURL: resolvedPhoto,
              image: resolvedPhoto
            });
            setLoggedIn(true);
          }

          const ok = await syncUserData({
            uid: uid,
            email: email,
            displayName: displayName,
            photoURL: resolvedPhoto,
            image: resolvedPhoto
          });

          if (ok) {
            await fetchData(uid);
          } else {
            console.log('Skipping fetch data due to sync failure.');
          }
        } catch (error) {
          if (isMounted) {
            setUser({
              uid: uid,
              email: email,
              displayName: displayName,
              photoURL: authPhotoURL,
              image: resolvedPhoto
            });
            setLoggedIn(true);
          }

          const ok2 = await syncUserData({
            uid: uid,
            email: email,
            displayName: displayName,
            photoURL: authPhotoURL,
            image: resolvedPhoto
          });

          if (ok2) {
            await fetchData(uid);
          } else {
            console.log('Skipping fetch data due to sync failure.');
          }
        }
      } else {
        if (isMounted) {
          setUser(null);
          setLoggedIn(false);
          setLoading(false);
        }
      }
    });

    // kick off bootstraps that do not depend on auth
    getFinancialSummary();
    robustGetServiceDetails();

    return function cleanup() {
      isMounted = false;
      unsubscribe();
    };
  }, [setLoggedIn, setRole]);

  // Balance toggle
  useEffect(() => {
    if (useBalance && remainingBalance > 0) {
      setRemainingBalance(remainingBalance - cartTotal);
    }
  }, [useBalance, remainingBalance, cartTotal]);

  // Onboarding banner
  useEffect(() => {
    if (user && (user.profileComplete !== undefined)) {
      var incomplete = !user.profileComplete;
      if (incomplete && !dismissed) setShowProfilePrompt(true);
    }
  }, [user, dismissed]);

  // Cart totals
  useEffect(() => {
    if (Array.isArray(cart) && cart.length > 0) {
      var i;
      var newTotalPrice = 0;
      var totalOriginalPrice = 0;
      for (i = 0; i < cart.length; i++) {
        var item = cart[i];
        newTotalPrice = newTotalPrice + (item.discountedPrice || 0);
        totalOriginalPrice = totalOriginalPrice + (item.originalPrice || 0);
      }
      setCartTotal(newTotalPrice);
      setCartOrigialTotal(totalOriginalPrice);
      setCartTotalDiscount(totalOriginalPrice - newTotalPrice);
    } else {
      setCartTotal(0);
      setCartOrigialTotal(0);
      setCartTotalDiscount(0);
    }
  }, [cart]);

  function closeProfilePrompt() {
    setShowProfilePrompt(false);
    localStorage.setItem('onboardingDismissed_v1', '1');
    setDismissed(true);
  }

  const handleAddPets = () => {
    setShowAddPetsModal(false);
    navigate('/profile');
    window.scrollTo(0, 0);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(API_BASE_URL + '/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log('Image URL:', response.data.imageUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  async function refreshUserData(uid) {
    setLoading(true);
    try {
      const userData = await fetchUserData(uid);

      // Build synthetic owner from the user row
      const userAsFamilyMember = {
        id: 'owner-synthetic',
        userId: userData.uid,
        name: userData.username ? userData.username.split(' ')[0] : 'Name',
        type: 'Human',
        image: userData.image || 'https://pawsible-s3.s3.us-west-1.amazonaws.com/user_files/defaultPerson.png'
      };

      // Keep only non-deleted members
      var filteredMembers = [];
      for (var i = 0; i < (userData.FamilyMembers || []).length; i++) {
        var m = userData.FamilyMembers[i];
        if (m && m.deletedAt === null) filteredMembers.push(m);
      }

      // If there is no persisted Human, prepend the synthetic one
      var hasHuman = false;
      for (var h = 0; h < filteredMembers.length; h++) {
        var t = filteredMembers[h] && filteredMembers[h].type ? String(filteredMembers[h].type).toLowerCase() : '';
        if (t === 'human') { hasHuman = true; break; }
      }
      if (!hasHuman) {
        filteredMembers.unshift(userAsFamilyMember);
      }

      // Sort with Human first, then by name
      filteredMembers.sort(function(a, b) {
        var ta = a && a.type ? String(a.type).toLowerCase() : '';
        var tb = b && b.type ? String(b.type).toLowerCase() : '';
        if (ta === 'human' && tb !== 'human') return -1;
        if (ta !== 'human' && tb === 'human') return 1;
        return String(a && a.name || '').localeCompare(String(b && b.name || ''));
      });

      // Convert bookings to local time
      var localBookings = [];
      for (var j = 0; j < (userData.Bookings || []).length; j++) {
        var bk = userData.Bookings[j];
        localBookings.push({
          ...bk,
          startTime: convertToUserTimeZone(bk.startTime),
          endTime: convertToUserTimeZone(bk.endTime)
        });
      }

      // Active payment methods
      var activePaymentMethods = [];
      for (var k = 0; k < (userData.PaymentMethods || []).length; k++) {
        var pm = userData.PaymentMethods[k];
        if (pm && pm.deletedAt === null) activePaymentMethods.push(pm);
      }

      const mappedUserData = {
        id: userData.id,
        uid: userData.uid,
        username: userData.username,
        email: userData.email,
        image: userData.image || '',
        photoURL: userData.image || '',
        phone: userData.phone,
        password: userData.password,
        createdAt: userData.createdat,
        updatedAt: userData.updatedat,
        daycareBundle: userData.DaycareBundles,
        clubMembership: userData.ClubMembership,
        wallet: userData.Wallet,
        userFriends: userData.UserFriends,
        cartItems: userData.CartItems,
        bookings: localBookings,
        familyMembers: filteredMembers, // <-- always includes a Human now
        paymentMethods: activePaymentMethods,
        stripeCustomerId: userData.stripeCustomerId,
        salesmateId: userData.salesmateId,
        defaultPaymentMethodId: userData.defaultPaymentMethodId,
        referralCode: userData.referralCode,
        referredBy: userData.referredBy,
        referralsCount: userData.referralsCount,
        storeLocation: userData.storeLocation,
        profileComplete: userData.profilecomplete,
        info: {
          email: userData.email || '',
          phone: userData.phone || '',
          addressOne: userData.addressOne || '',
          addressTwo: userData.addressTwo || '',
          city: userData.city || '',
          state: userData.state || '',
          zipCode: userData.zipCode || ''
        }
      };

      setUser(function(prevData) {
        var merged = {};
        for (var key in prevData) { merged[key] = prevData[key]; }
        for (var key2 in mappedUserData) { merged[key2] = mappedUserData[key2]; }
        return merged;
      });
      setCart(userData.CartItems);
      setPaymentMethods(activePaymentMethods);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch user data:', error.response || error);
      setLoading(false);
    }
  }

  return (
    <StateContext.Provider
      value={{
        role,
        user,
        setUser,
        loggedIn,
        setLoggedIn,
        setLoading,
        serviceDetails,
        financialSummary,
        cart,
        setCart,
        cartTotal,
        cartOriginalTotal,
        cartTotalDiscount,
        bookingData,
        setBookingData,
        handleFileChange,
        handleUpload,
        refreshUserData,
        paymentMethods,
        setPaymentMethods,
        setShowAddPetsModal,
        remainingBalance,
        setRemainingBalance,
        useBalance,
        setUseBalance
      }}
    >
      <Spinner isActive={loading} />

      <OnboardingProfilePrompt
        open={showProfilePrompt}
        onClose={closeProfilePrompt}
      />

      {showAddPetsModal && (
        <AddPetPrompt
          open={showAddPetsModal}
          onClose={() => setShowAddPetsModal(false)}
        />
      )}

      {children}
    </StateContext.Provider>
  );
};

export default StateProvider;
